export const enHubspot = {
	title: "HubSpot Integration",
	connected: "Connected",
	notConnected: "Not Connected",
	connect: "Connect",
	disconnect: "Disconnect",
	testConnection: "Test Connection",
	configureFieldMapping: "Configure Field Mapping",
	fieldMapping: {
		title: "HubSpot Field Mapping",
		contacts: "Contacts",
		companies: "Companies",
		deals: "Deals",
		leads: "Leads",
		ourField: "Signalist Field",
		hubspotField: "HubSpot Field",
		active: "Active",
		actions: "Actions",
		addMapping: "Add Mapping",
		saveAll: "Save All",
		selectField: "Select a field",
		noMappings: "No field mappings configured yet.",
		deleteConfirm: "Are you sure you want to delete this mapping?",
		sourceTypeFromRecord: "From record",
		sourceTypeConstant: "Constant value",
		sourceTypeAi: "AI-generated",
		constantPlaceholder: "Enter a literal value",
		constantLabel: 'Constant: "{{value}}"',
		aiBadge: "AI",
		aiConfigure: "Configure",
		aiTargetPlaceholder: "Pick a HubSpot field →",
		aiCreditTooltip: "0.1 credits per lead synced — flat, covers all AI fields",
		aiHintTitle: "AI-enriched fields",
		aiHintBody:
			"Let an AI agent classify, research, or extract a value before syncing. AI enrichment costs 0.1 credits per lead — flat, regardless of how many AI fields you add. The field name will match your selected HubSpot target.",
		aiSaveDisabledTooltip: "Configure AI to save",
		cannotMap: "— Cannot map —",
		cannotMapTooltip: "HubSpot calculated field — Signalist cannot write to this",
		constantNotAnOption: "Not an option of this HubSpot field",
		constantNotAnOptionTooltip:
			"HubSpot rejects every synced record while this value is not one of the field's options. Allowed: {{options}}",
		saveFailed: "Saving the mapping failed. Please try again.",
		leadsScopeHint:
			"Your HubSpot connection is missing the Leads permission, so Lead fields cannot be loaded. Reconnect HubSpot on the Connection tab (a private app needs the crm.objects.leads scopes).",
		metaBadge: {
			required: "Required",
			multi: "Multi",
			dropdown: "Dropdown",
			number: "Number",
			calculated: "Calculated",
			readOnly: "Read-only",
			createOnly: "Create-only",
			createOnlyTooltip:
				"HubSpot only accepts this field when the record is first created. Signalist fills it for contacts it creates; contacts that already exist in HubSpot keep their current value.",
		},
		footerSummary: "{{mapped}} fields mapped · {{ai}} AI-enriched",
		footerCreditPill: "+0.1 credits per lead with AI enrichment",
		aiConfigTitle: "Configure AI-enriched field",
		aiConfigSubtitle: "Target: {{label}}",
		aiConfigPlaceholder:
			"Prompt editor, allowed values, behavior options and test panel will land in the next iteration.",
		aiConfig: {
			configurationTitle: "Configuration",
			promptLabel: "Prompt",
			promptPlaceholder: "Tell the AI how to generate this CRM field value...",
			promptHelp:
				"The AI runs once per sync with this prompt and the prospect and organization data as input.",
			promptMeta: "Editable · pre-filled from field metadata",
			defaultPrompt:
				"Generate a value for {{target}}. Use the available prospect and organization context. Allowed values: {{allowedValues}}. Return only the final value.",
			insertVariable: "Insert variable",
			variablesLabel: "Variables",
			allowedValuesTitle: "Allowed values",
			allowedValuesEmpty: "No allowed values returned for this field.",
			fallbackLabel: "Fallback value",
			fallbackHelp:
				"Used when the AI cannot determine a confident value. For dropdowns, the fallback must match one of the allowed values.",
			leaveEmpty: "Leave empty",
			behaviorTitle: "Behavior",
			syncReasoningLabel: "Sync reasoning",
			syncReasoningHelp: "Write the AI reasoning to another text field.",
			syncReasoningTargetLabel: "Sync reasoning to",
			syncReasoningTargetHelp: "Only text and textarea fields can receive reasoning.",
			syncSourceLabel: "Sync source",
			syncSourceHelp: "Write the source list to another text field.",
			syncSourceTargetLabel: "Sync source to",
			syncSourceTargetHelp: "Only text and textarea fields can receive sources.",
			secondaryTargetPlaceholder: "Select a text field",
			testTitle: "Test",
			freeDuringSetup: "Free during setup",
			testIntroTitle: "Test this prompt with a prospect from your pipeline",
			testIntroHelp:
				"Load the variables for a prospect, adjust values if needed, and run a free test.",
			prospectLabel: "Prospect",
			prospectPlaceholder: "Search prospects...",
			loadVariables: "Load variables",
			variablesPreviewTitle: "Variables preview",
			noVariables: "Pick a prospect and load variables to preview the prompt data.",
			editData: "Edit data",
			revertEdits: "Revert edits",
			runTest: "Run test",
			rerunTest: "Re-run test",
			testRunHelp: "Test runs are free, do not update the CRM, and do not consume credits.",
			progressLabel: "Load lead data → analyze & match → return result",
			loadingLeadData: "Load prospect data",
			loadingResearch: "Research organization",
			loadingAnalyze: "Analyze & match",
			loadingReturn: "Return result",
			resultTitle: "Result",
			resultReadyTitle: "AI returned a value",
			valueLabel: "Value",
			reasoningLabel: "Reasoning",
			sourcesLabel: "Sources",
			durationLabel: "Duration",
			fallbackUsed: "Fallback was used.",
			finalPrompt: "Final prompt",
			emptyState: "No test run yet.",
			noCredits: "Tests do not consume credits and do not write to the CRM.",
			costReminder: "0.1 credits per lead synced (flat — covers all AI fields)",
			cancel: "Cancel",
			save: "Save AI mapping",
			requiredPrompt: "Prompt is required",
			requiredSecondaryField: "Select a text field",
			invalidFallback: "Fallback must be one of the allowed values",
			testError: "AI test failed. Please try again.",
		},
	},
	validation: {
		valid: "Connection successful",
		invalid: "Connection failed",
		testing: "Testing connection...",
	},
	tabs: {
		connection: "Connection",
		settings: "Settings",
		dealConfiguration: "Deal Configuration",
		leadConfiguration: "Lead Configuration",
		userMapping: "User Mapping",
		syncLogs: "Sync Logs",
	},
	syncLogs: {
		description:
			"The history of syncs from Signalist to HubSpot. A failed row shows HubSpot's answer for the first record it could not sync.",
		filterByEntity: "Filter by Entity",
		allEntities: "All Entities",
		entityType: "Entity Type",
		status: "Status",
		synced: "Synced",
		failed: "Failed",
		error: "Error",
		startedAt: "Started At",
		completedAt: "Completed At",
		noLogs: "No sync logs found.",
		pageOf: "{{from}}–{{to}} of {{total}}",
	},
	syncTrigger: {
		title: "Sync Prospects to HubSpot",
		description: "Choose when prospects should be automatically synchronized to HubSpot.",
		DISABLED: "Disabled",
		ON_CAMPAIGN_START: "When campaign starts",
		ON_REPLY: "When prospect replies",
		INHERIT: "Use company default",
		inheritedFrom: "Currently using company setting",
	},
	dealCreationTrigger: {
		title: "Automatic Deal Creation",
		description:
			"Choose when deals should be automatically created in HubSpot for leads that already exist.",
		DISABLED: "Off",
		ON_RESPONSE: "On Response",
		ON_POSITIVE_RESPONSE: "On Positive Response",
	},
	leadSyncTrigger: {
		title: "Automatic Lead Creation",
		description: "Choose when HubSpot Leads should be created independently from contact sync.",
		DISABLED: "Off",
		ON_RESPONSE: "On Response",
		ON_POSITIVE_RESPONSE: "On Positive Response",
		INHERIT: "Use company default",
		inheritedFrom: "Currently using company setting",
	},
	dealConfig: {
		title: "Deal Defaults",
		description: "Configure default values for new HubSpot deals.",
		defaultPipeline: "Default Pipeline",
		defaultStage: "Default Stage",
		dealNameTemplate: "Deal Name Template",
		dealNameTemplateHelp: "Use {firstName}, {lastName}, {company} as placeholders.",
		closeDateOffsetDays: "Close Date Offset (days)",
		closeDateOffsetHelp: "Number of days from today for the default close date.",
		defaultOwner: "Default Deal Owner",
		// The order is pickHubspotDealOwnerId's in the API (PREC-4300).
		defaultOwnerHelp:
			"Owns an automatic deal when neither the sender's user mapping nor the HubSpot company's owner names one. It also pre-fills the owner when you create a deal by hand.",
		defaultLifecycleStage: "Default Lifecycle Stage",
		defaultLeadStatus: "Default Lead Status",
		selectPipeline: "Select a pipeline",
		selectStage: "Select a stage",
		selectOwner: "Select an owner",
		saved: "Deal configuration saved successfully.",
		saveError: "Failed to save deal configuration.",
	},
	leadConfig: {
		description: "Configure default values used when Signalist creates HubSpot Leads.",
		defaultPipeline: "Default Lead Pipeline",
		defaultStage: "Default Lead Stage",
		leadNameTemplate: "Lead Name Template",
		leadNameTemplateHelp: "Use {firstName}, {lastName}, {company} as placeholders.",
		defaultOwner: "Default Lead Owner",
		defaultLeadType: "Default Lead Type",
		defaultLeadLabel: "Default Lead Label",
		noLeadTypeOptions: "HubSpot did not return selectable Lead Type options.",
		noLeadLabelOptions: "HubSpot did not return selectable Lead Label options.",
		missingScopeHint:
			"Your HubSpot connection is missing the Leads permission, so Lead pipelines, types and labels cannot be loaded. Reconnect HubSpot on the Connection tab (a private app needs the crm.objects.leads scopes).",
		existingLeadBehaviorLabel: "Existing Active Lead Behavior",
		existingLeadBehavior: {
			SKIP: "Skip creation",
			UPDATE: "Update existing Lead",
			CREATE_NEW: "Create a new Lead",
		},
		saved: "Lead configuration saved successfully.",
		saveError: "Failed to save lead configuration.",
	},
	userMapping: {
		title: "User Mapping",
		description: "Map Signalist users to HubSpot owners for deal assignment.",
		signalistUser: "Signalist User",
		hubspotOwner: "HubSpot Owner",
		selectOwner: "Select an owner",
		noUsers: "No sending users found.",
		saved: "User mappings saved successfully.",
		saveError: "Failed to save user mappings.",
	},
	createDeal: {
		title: "Create HubSpot Deal",
		buttonLabel: "Create Deal",
		dealName: "Deal Name",
		pipeline: "Pipeline",
		stage: "Deal Stage",
		owner: "Deal Owner",
		closeDate: "Close Date",
		selectPipeline: "Select a pipeline",
		selectStage: "Select a stage",
		selectOwner: "Select an owner",
		success: "Deal created successfully in HubSpot.",
		error: "Failed to create deal in HubSpot.",
		missingConfig: "Please configure deal defaults in HubSpot settings first.",
	},
	conversationSync: {
		title: "Conversation Sync",
		label: "Sync conversations to HubSpot",
		description:
			"When enabled, messages sent to and received from prospects are automatically synced as notes in HubSpot.",
		success: "Conversation sync setting updated successfully.",
		error: "Failed to update conversation sync setting.",
	},
	resync: {
		title: "Resync with HubSpot",
		description:
			"Re-push every already-synced contact and company to HubSpot using your current field mapping. Useful after you add or change mapped fields.",
		button: "Resync now",
		confirmTitle: "Resync with HubSpot?",
		confirmBody:
			"This will re-push every contact and company that is already synced to HubSpot using your current field mapping. Continue?",
		confirmCta: "Resync",
		success: "Resync queued — {{count}} contact(s) will be re-pushed to HubSpot.",
		empty: "No already-synced contacts found.",
		error: "Failed to queue resync. Please try again.",
	},
	oauth: {
		connectWithHubspot: "Connect with HubSpot",
		upgradeToOAuth: "Upgrade to OAuth",
		connectedViaOAuth: "Connected via OAuth",
		connectedViaApiKey: "Connected via API Key (Legacy)",
		portalId: "Portal",
		tokenRefresh: "Token refreshes automatically",
		disconnectTitle: "Disconnect HubSpot?",
		disconnectBody:
			"This will remove the OAuth connection. HubSpot syncing will stop unless a Private App API key is configured.",
		connectError: "Failed to generate authorization URL",
		advancedApiKey: "Advanced: Connect via API Key",
		reauthTitle: "Reconnect required",
		reauthDescription:
			"Your HubSpot connection is missing a required permission (Leads). Reconnect HubSpot to restore full syncing.",
		reconnect: "Reconnect",
	},
	callback: {
		successTitle: "HubSpot Connected Successfully!",
		successBody:
			"Your HubSpot account has been connected. Redirecting to settings in {{countdown}}s...",
		goToSettings: "Go to Settings",
		errorTitle: "Connection Failed",
		errorBody: "Something went wrong while connecting to HubSpot. Please try again.",
		tryAgain: "Try Again",
	},
};
